import React, { useContext } from 'react';
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { Context } from 'Context/Context';
import ScaleOneToTen from 'Components/Element/ScaleOneToTen';
import CreateCaseAnonymous from './CreateCase/CreateCaseAnonymous';
import CaseLocationSameCheckbox from './CreateCase/CaseLocationSameCheckbox';
import CaseCategoryIcon from './CreateCase/CaseCategoryIcon';
import FieldError from 'Components/Element/FieldError';

const inputCls = (hasError) =>
  "block w-full box-border rounded-lg border-[1.5px] py-3.5 px-3.5 text-[16px] text-navy bg-ivory placeholder:text-[rgba(20,33,61,.35)] focus:outline-none font-plexsans " +
  (hasError ? "border-red-500 focus:border-red-500" : "border-[rgba(20,33,61,.18)] focus:border-tan");

const labelCls = "block font-medium text-[14.5px] leading-none text-navy mb-2 font-plexsans";

const RecursiveContainer = ({field, formValues, onChangeUpdate, errors, onBlurValidate}) => {
  const {states} = useContext(Context);

  const fireSelect = (individualConfig, value) => {
    onChangeUpdate({ target: { value, name: individualConfig.name } }, individualConfig);
    onBlurValidate({ target: { value, name: individualConfig.name } }, individualConfig);
  };

  const builder = (individualConfig) => {
      switch (individualConfig.type) {
      case 'input':
        return (
                <div className='w-full'>
                  <label className={labelCls}>
                    {individualConfig.label}
                    {individualConfig.hoverInfo ? <span className='font-normal text-xs ml-1 text-[rgba(20,33,61,.45)]'>{" - " + individualConfig.hoverInfo}</span> : ''}
                  </label>
                  <input type='text'
                    onBlur={(e) => onBlurValidate(e, individualConfig)}
                    disabled={individualConfig.disabled}
                    onChange={(e) => (individualConfig.disabled ? '' : onChangeUpdate(e, individualConfig))}
                    value={formValues?.[individualConfig?.name] ? formValues?.[individualConfig?.name] : ""}
                    className={inputCls(errors?.[individualConfig.name]) + (individualConfig.disabled ? " opacity-60 cursor-not-allowed" : "")}
                    name={individualConfig.name}
                    placeholder={individualConfig.placeholder}
                    inputMode={(individualConfig.name === "clientZipcode" || individualConfig.name === "caseZipcode") ? "numeric" : undefined}
                    maxLength={(individualConfig.name === "clientZipcode" || individualConfig.name === "caseZipcode") ? 10 : undefined}
                     />
                  {errors?.[individualConfig?.name] ? (
                      <FieldError>{errors?.[individualConfig.name]}</FieldError>
                  ): ""}
                </div>
              );
      case 'number':
        return (
                <div className='w-full'>
                  <label className={labelCls}>{individualConfig.label}</label>
                  <input type='tel'
                    onBlur={(e) => onBlurValidate(e, individualConfig)}
                    onChange={(e) => onChangeUpdate(e, individualConfig)}
                    maxLength={10}
                    value={formValues?.[individualConfig.name] ? formValues?.[individualConfig.name] : ""}
                    className={inputCls(errors?.[individualConfig.name])}
                    name={individualConfig.name}
                    placeholder={individualConfig.placeholder}
                     />
                  {errors?.[individualConfig.name] ? (
                      <FieldError>{errors?.[individualConfig.name]}</FieldError>
                  ): ""}
                </div>
              );
      case 'textArea':
        return (
                <div className='w-full'>
                  <label htmlFor={individualConfig.name} className={labelCls}>{individualConfig.label}</label>
                  <textarea
                      maxLength="500"
                      className={inputCls(false) + " resize-none h-[80px]"}
                      name={individualConfig.name}
                      value={formValues?.[individualConfig.name] ? formValues?.[individualConfig.name] : ""}
                      onChange={(e) => onChangeUpdate(e, individualConfig)}
                      placeholder={individualConfig.placeholder}
                      />
                </div>
              );
      case 'select': {
        const rawOptions = individualConfig.options && individualConfig.options.length ? individualConfig.options : states;
        const isNativeStateDropdown = !individualConfig.options || individualConfig.options.length === 0;
        const opts = (rawOptions || []).map((opt) => ({
          id: opt?.id !== undefined ? opt.id : opt,
          name: opt?.name !== undefined ? opt.name : opt,
        }));
        const currentValue = formValues?.[individualConfig.name] ? formValues?.[individualConfig.name] : "";
        const maxLen = opts.reduce((m, o) => Math.max(m, String(o.name).length), 0);
        // Only the true unbounded lists (US states, via Context) fall back
        // to a native <select> — every field that ships its own `options`
        // (including the 10-item case category list) gets the card/pill
        // picker so it matches the design's descriptive choice cards.
        const useNativeDropdown = isNativeStateDropdown;
        const stack = !useNativeDropdown && maxLen > 20;

        if (useNativeDropdown) {
          return (
            <div className='w-full'>
              <label className={labelCls} htmlFor={individualConfig.name}>{individualConfig.label}</label>
              <div className="relative">
                <select
                  name={individualConfig.name}
                  value={currentValue}
                  onChange={(e) => onChangeUpdate(e, individualConfig)}
                  onBlur={(e) => onBlurValidate(e, individualConfig)}
                  className={inputCls(errors?.[individualConfig.name]) + " appearance-none pr-9 cursor-pointer"}>
                    <option value="">Choose</option>
                    {opts.map((opt) => <option key={String(opt.id)} value={opt.id}>{opt.name}</option>)}
                </select>
                <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(20,33,61,.45)" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </div>
              {errors?.[individualConfig.name] ? (
                <FieldError>{errors?.[individualConfig.name]}</FieldError>
              ): ""}
            </div>
          );
        }

        const withIcons = individualConfig.name === "caseType";
        const groupCls = withIcons
          ? "grid grid-cols-1 lg:grid-cols-2 gap-2"
          : (stack ? "flex flex-col gap-2" : "flex flex-wrap gap-2");

        return (
          <div className='w-full'>
            <label className={labelCls}>{individualConfig.label}</label>
            <div className={groupCls}>
              {opts.map((opt) => {
                const selected = String(currentValue) === String(opt.id);
                return (
                  <button
                    type="button"
                    key={String(opt.id)}
                    onClick={() => fireSelect(individualConfig, opt.id)}
                    className={
                      (withIcons ? "flex items-center gap-2.5 text-left " : (stack ? "w-full text-left " : "flex-1 min-w-[90px] text-center ")) +
                      "px-4 py-3 rounded-lg border-[1.5px] font-medium text-[14.5px] leading-snug transition-colors font-plexsans " +
                      (selected
                        ? "border-tan bg-[rgba(138,98,49,.08)] text-tan font-semibold"
                        : "border-[rgba(20,33,61,.15)] text-navy hover:border-[rgba(20,33,61,.3)]")
                    }
                  >
                    {withIcons && <CaseCategoryIcon id={opt.id} active={selected} />}
                    <span>{opt.name}</span>
                  </button>
                );
              })}
            </div>
            {errors?.[individualConfig.name] ? (
                <FieldError>{errors?.[individualConfig.name]}</FieldError>
            ): ""}
          </div>
        );
      }
      case 'date': {
        return (
          <div className='w-full qcr-datepicker'>
                  <label htmlFor={individualConfig.name} className={labelCls}>{individualConfig.label}</label>
                  <div className='relative'>
                    <DatePicker
                      name={individualConfig.name}
                      maxDate={new Date()}
                      required={false}
                      value={formValues?.[individualConfig.name] ? new Date(formValues?.[individualConfig.name]) : null}
                      selected={formValues?.[individualConfig.name]}
                      onChange={(e) => onChangeUpdate(e, individualConfig)}
                      wrapperClassName='bg-ivory w-full'
                      className={"w-full focus:outline-none rounded-lg border-[1.5px] " + (errors?.[individualConfig.name] ? "border-red-500" : "border-[rgba(20,33,61,.18)]")}
                    />
                  </div>
              {errors?.[individualConfig.name] ? (
              <FieldError>{errors?.[individualConfig.name]}</FieldError>
          ): ""}
          </div>
            );
      }
      case 'fileUpload' :
      return (
          <div className='flex flex-col gap-2 w-full lg:mt-4'>
              <label className="font-medium text-[14.5px] text-navy font-plexsans" htmlFor={individualConfig.name}>{individualConfig.label}</label>
                  <input type='file'
                    id={individualConfig.name}
                    accept="image/*,.doc,.docx,.pdf,.mp4"
                    multiple="multiple"
                    name={individualConfig.name}
                    onChange={(e) => onChangeUpdate(e,individualConfig)}
                    className="text-sm text-[rgba(20,33,61,.6)] font-plexsans
                     file:py-2 file:px-4 file:border-0
                    file:font-medium file:text-sm
                    file:bg-tan
                    file:rounded-lg
                    file:text-ivory
                    hover:file:cursor-pointer hover:file:bg-tanDark"
                    />
        </div>
      )
      case 'checkbox' :
      return (
        <div className="w-full">
        <label
            className={"flex items-start gap-2.5 p-3.5 rounded-lg border-[1.5px] cursor-pointer transition-colors " + (formValues?.[individualConfig.name] ? "border-tan bg-[rgba(138,98,49,.08)]" : "border-[rgba(20,33,61,.15)]")}
          >
          <input
              type="checkbox"
              name={individualConfig.name}
              checked={!!formValues?.[individualConfig.name]}
              onBlur={(e) => onBlurValidate(e, individualConfig)}
              onChange={(e) => onChangeUpdate(e,individualConfig)}
              className="mt-0.5 accent-tan h-4 w-4 flex-none"
          />
          <span className="font-medium text-[14.5px] text-navy font-plexsans">{individualConfig.label}</span>
      </label>
        {errors?.[individualConfig.name] ? (
              <FieldError>{errors?.[individualConfig.name]}</FieldError>
          ): ""}
        </div>
      )
      case 'ratingBar' :
      return (
         <div className='mb-1 flex flex-col border-[1.5px] border-[rgba(20,33,61,.15)] rounded-lg p-4 w-full bg-[rgba(20,33,61,.02)]'>
              <div className="font-semibold text-[15px] text-navy font-plexsans">
                  {individualConfig.label}
              </div>
              <div className="text-xs text-[rgba(20,33,61,.5)] italic mt-1 font-plexsans">{individualConfig.example}</div>
              <ScaleOneToTen selected={formValues?.[individualConfig.name]}
              onChangeUpdateFunc={onChangeUpdate}
              formValObj={individualConfig.name}
              />
        </div>
      )
      case 'anonymousCheckbox':
        return (
          <CreateCaseAnonymous
          field={field}
          formValues={formValues}
          onChangeUpdate={onChangeUpdate}
          errors={errors}
          onBlurValidate={onBlurValidate}
          />
        )
      case 'sameLocationCheckbox':
        return (
          <CaseLocationSameCheckbox
          field={individualConfig}
          formValues={formValues}
          onChangeUpdate={onChangeUpdate}
          errors={errors}
          onBlurValidate={onBlurValidate}
          />
        )
      case 'checkboxGroup': {
        const opts = (individualConfig.options || []).map((opt) => ({
          id: opt?.id !== undefined ? opt.id : opt,
          name: opt?.name !== undefined ? opt.name : opt,
        }));
        const current = Array.isArray(formValues?.[individualConfig.name]) ? formValues[individualConfig.name] : [];
        const cols = opts.length > 4 ? "lg:grid-cols-3" : "lg:grid-cols-2";
        const toggle = (id) => {
          const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
          onChangeUpdate(next, individualConfig);
        };
        return (
          <div className='w-full'>
            <label className={labelCls}>{individualConfig.label}</label>
            <div className={`grid grid-cols-1 ${cols} gap-2`}>
              {opts.map((opt) => {
                const selected = current.includes(opt.id);
                return (
                  <button
                    type="button"
                    key={String(opt.id)}
                    onClick={() => toggle(opt.id)}
                    className={
                      "flex items-center gap-2 px-3 py-2.5 rounded-lg border-[1.5px] text-left transition-colors font-plexsans " +
                      (selected
                        ? "border-tan bg-[rgba(138,98,49,.08)]"
                        : "border-[rgba(20,33,61,.15)] hover:border-[rgba(20,33,61,.3)]")
                    }
                  >
                    <span className={
                      "flex-none w-4 h-4 rounded-[3px] flex items-center justify-center text-[10px] font-semibold " +
                      (selected ? "bg-tan text-ivory" : "border-[1.5px] border-[rgba(20,33,61,.25)]")
                    }>
                      {selected ? "✓" : ""}
                    </span>
                    <span className="text-[13.5px] font-medium text-navy leading-snug">{opt.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      default:
        return <div>Unsupported field</div>
    }
  }

  return (
    <>
    {builder(field)}
    </>
  );
};

export default RecursiveContainer;
